import { LinearGradient } from 'expo-linear-gradient';
import { Pressable } from 'react-native';
import { Box, HStack, Text } from '../../../components/primitives';
import { useTranslatedStageName } from '../../../hooks/useTranslatedStageName';
import { colors } from '../../../theme/colors';
import { shadows } from '../../../theme/shadows';
import { tokens } from '../../../theme/tokens';
import type { FunnelStage } from '../../../types/leads';

interface StageProgressProps {
  stages: FunnelStage[];
  currentStageId: string;
  onStagePress?: (stageId: string) => void;
}

export function StageProgress({
  stages,
  currentStageId,
  onStagePress,
}: Readonly<StageProgressProps>) {
  const translateStageName = useTranslatedStageName();
  const currentIndex = stages.findIndex((s) => s.id === currentStageId);

  const lastIndex = Math.max(stages.length - 1, 1);
  const safeIndex = currentIndex < 0 ? 0 : currentIndex;
  const fillPct = safeIndex <= 0 ? 6 : (safeIndex / lastIndex) * 100;

  return (
    <Box>
      <Box
        height={6}
        borderRadius="full"
        backgroundColor={colors.surface.row}
        style={{ width: '100%', marginBottom: 16, position: 'relative', overflow: 'visible' }}
      >
        <Box
          borderRadius="full"
          style={[
            { position: 'absolute', left: 0, top: 0, height: 6, width: `${fillPct}%` },
            shadows.glowHaloSoft,
          ]}
        >
          <LinearGradient
            colors={colors.primary.gradient}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={{ flex: 1, borderRadius: tokens.radius.full }}
          />
        </Box>
      </Box>

      <HStack justify="between" style={{ flexWrap: 'nowrap' }}>
        {stages.map((stage, index) => {
          const isActive = stage.id === currentStageId;
          const isPast = index < currentIndex;

          const resolveTextColor = () => {
            if (isActive) return colors.primary[400];
            if (isPast) return colors.text.secondary;
            return colors.text.tertiary;
          };

          return (
            <Pressable
              key={stage.id}
              onPress={() => onStagePress?.(stage.id)}
              style={{ flexShrink: 1 }}
            >
              <Text
                variant="caption"
                color={resolveTextColor()}
                style={{
                  fontWeight: isActive ? '700' : '500',
                  textAlign: 'center',
                }}
                numberOfLines={1}
              >
                {translateStageName(stage.name)}
              </Text>
            </Pressable>
          );
        })}
      </HStack>
    </Box>
  );
}
